import {
  FILE_LOADED,
  NEW_FILE,
  ADD_CHARACTER_CATEGORY,
  DELETE_CHARACTER_CATEGORY,
  UPDATE_CHARACTER_CATEGORY,
  REORDER_CHARACTER_CATEGORY,
  ADD_NOTE_CATEGORY,
  DELETE_NOTE_CATEGORY,
  UPDATE_NOTE_CATEGORY,
  REORDER_NOTE_CATEGORY,
  ADD_TAG_CATEGORY,
  DELETE_TAG_CATEGORY,
  UPDATE_TAG_CATEGORY,
  REORDER_TAG_CATEGORY,
  ADD_PLACE_CATEGORY,
  DELETE_PLACE_CATEGORY,
  UPDATE_PLACE_CATEGORY,
  REORDER_PLACE_CATEGORY,
  ACTION_CONFLICT_CHANGES,
  LOAD_CATEGORIES,
  UNDO,
  REDO,
  UNDO_N_TIMES,
  REDO_N_TIMES,
} from '../constants/ActionTypes'
import { newFileCategories } from '../store/newFileState'
import { categories as defaultCategories } from '../store/initialState'
import { nextId } from '../store/newIds'
import { positionReset } from '../helpers/lists'

/**
 * @template A
 * @typedef {import('../types/reducer').DataRepairer<A>} DataRepairer
 */

/**
 * @typedef {import('../index').PltrAction} PltrAction
 * @typedef {import('../index').FileCategories} FileCategories
 */

/**
 * @param {DataRepairer<any>} _dataRepairers
 * @return {function(FileCategories, PltrAction): FileCategories}
 */
const categories =
  (_dataRepairers) =>
  (state = defaultCategories, action) => {
    switch (action.type) {
      case FILE_LOADED:
        return action.data.categories

      case NEW_FILE:
        return newFileCategories()

      case ADD_NOTE_CATEGORY:
        return addCategory('notes', state, action)

      case DELETE_NOTE_CATEGORY:
        return deleteCategory('notes', state, action.category.id)

      case UPDATE_NOTE_CATEGORY:
        return updateCategory('notes', state, action)

      case REORDER_NOTE_CATEGORY:
        return reorderCategory('notes', state, action)

      case ADD_CHARACTER_CATEGORY:
        return addCategory('characters', state, action)

      case DELETE_CHARACTER_CATEGORY:
        return deleteCategory('characters', state, action.category.id)

      case UPDATE_CHARACTER_CATEGORY:
        return updateCategory('characters', state, action)

      case REORDER_CHARACTER_CATEGORY:
        return reorderCategory('characters', state, action)

      case ADD_TAG_CATEGORY:
        return addCategory('tags', state, action)

      case DELETE_TAG_CATEGORY:
        return deleteCategory('tags', state, action.category.id)

      case UPDATE_TAG_CATEGORY:
        return updateCategory('tags', state, action)

      case REORDER_TAG_CATEGORY:
        return reorderCategory('tags', state, action)

      case ADD_PLACE_CATEGORY:
        return addCategory('places', state, action)

      case DELETE_PLACE_CATEGORY:
        return deleteCategory('places', state, action.category.id)

      case UPDATE_PLACE_CATEGORY:
        return updateCategory('places', state, action)

      case REORDER_PLACE_CATEGORY:
        return reorderCategory('places', state, action)

      case ACTION_CONFLICT_CHANGES: {
        return Object.entries(action.mergedCategories).reduce((acc, next) => {
          const [key, value] = next
          const { mergedCategories } = value
          return {
            ...acc,
            [key]: mergedCategories,
          }
        }, state)
      }

      case UNDO_N_TIMES:
      case REDO_N_TIMES:
      case UNDO:
      case REDO: {
        if (action?.state?.categories && typeof action.state.categories === 'object') {
          return action.state.categories
        } else {
          return state
        }
      }

      case LOAD_CATEGORIES:
        return action.categories

      default:
        return state
    }
  }

function addCategory(key, state, action) {
  const id = nextId(state[key])
  return {
    ...state,
    [key]: [
      ...state[key],
      {
        id,
        name: action.name,
        position: state[key].length,
      },
    ],
  }
}

function deleteCategory(key, state, categoryId) {
  const index = state[key].findIndex((item) => item.id === categoryId)
  if (index == -1) return state

  const newArray = [...state[key].slice(0, index), ...state[key].slice(index + 1)]
  newArray.forEach((category, i) => (category.position = i))

  return {
    ...state,
    [key]: newArray,
  }
}

function updateCategory(key, state, action) {
  const index = state[key].findIndex((item) => item.id === action.category.id)
  if (index === -1) return state

  const newArray = [...state[key]]
  newArray[index] = {
    ...state[key][index],
    ...action.category,
  }

  return {
    ...state,
    [key]: newArray,
  }
}

function reorderCategory(key, state, { toIndex, category }) {
  const copy = state[key].slice().filter(({ id }) => id != category.id)

  copy.splice(toIndex, 0, category)
  const newList = positionReset(copy) // have to change the value of the position attribute
  return {
    ...state,
    [key]: newList,
  }
}

export default categories
